/**
 * The asset grid, shared by the library page and the picker.
 *
 * Two independent interactions on one tile: clicking it opens/chooses the
 * asset, and the checkbox toggles it into a multi-select batch. The checkbox
 * stops propagation so ticking never also opens the detail modal.
 */

import { CheckIcon, ExclamationTriangleIcon, LinkIcon } from '@heroicons/react/24/solid';

import { Badge } from '@/components/common';
import { cn } from '@/utils';
import { formatBytes, formatDimensions, type MediaAsset } from '@/services/assets';

import { AssetThumb } from './AssetThumb';

interface AssetGridProps {
  assets: MediaAsset[];
  isLoading?: boolean;
  /** Ids currently ticked. Presence of this prop turns the checkboxes on. */
  selectedIds?: string[];
  onToggleSelect?: (asset: MediaAsset) => void;
  onOpen?: (asset: MediaAsset) => void;
  /** Ring-highlighted id — used by the picker for the current choice. */
  activeId?: string | null;
  /** Fewer meta lines per tile, for the picker's tighter grid. */
  compact?: boolean;
  emptyState?: React.ReactNode;
  columnsClassName?: string;
}

export function AssetGrid({
  assets,
  isLoading,
  selectedIds,
  onToggleSelect,
  onOpen,
  activeId,
  compact = false,
  emptyState,
  columnsClassName = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
}: AssetGridProps) {
  if (isLoading) {
    return (
      <div className={cn('grid gap-4', columnsClassName)}>
        {Array.from({ length: 10 }).map((_, index) => (
          <div
            key={index}
            className="animate-pulse overflow-hidden rounded-xl border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700" />
            <div className="space-y-2 p-3">
              <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-gray-700/60" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!assets.length) {
    return <>{emptyState}</>;
  }

  const selected = new Set(selectedIds || []);

  return (
    <div className={cn('grid gap-4', columnsClassName)}>
      {assets.map((asset) => {
        const isSelected = selected.has(asset.id);
        const isActive = activeId === asset.id;
        return (
          <div
            key={asset.id}
            className={cn(
              'group relative overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-all dark:bg-gray-800',
              isActive || isSelected
                ? 'border-primary-500 ring-2 ring-primary-500/40'
                : 'border-gray-100 hover:border-primary-200 hover:shadow-md dark:border-gray-700 dark:hover:border-primary-800',
            )}
          >
            <button
              type="button"
              onClick={() => onOpen?.(asset)}
              className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              title={asset.original_filename}
            >
              <div className="aspect-[4/3] w-full bg-gray-50 dark:bg-gray-900">
                <AssetThumb asset={asset} />
              </div>
              <div className="space-y-1 p-3">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {asset.title || asset.original_filename}
                </p>
                {!compact && (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {formatDimensions(asset)} · {formatBytes(asset.bytes)} · {asset.extension.toUpperCase()}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1 pt-0.5">
                  {asset.is_platform && (
                    <Badge variant="info" size="sm">
                      Shared
                    </Badge>
                  )}
                  {asset.in_use ? (
                    <Badge variant="success" size="sm">
                      <LinkIcon className="mr-1 h-3 w-3" />
                      {asset.usage_count} use{asset.usage_count === 1 ? '' : 's'}
                    </Badge>
                  ) : (
                    <Badge variant="default" size="sm">
                      Unused
                    </Badge>
                  )}
                  {!asset.alt_text && (
                    <Badge variant="warning" size="sm">
                      <ExclamationTriangleIcon className="mr-1 h-3 w-3" />
                      No alt text
                    </Badge>
                  )}
                </div>
              </div>
            </button>

            {selectedIds && onToggleSelect && (
              <label
                className={cn(
                  'absolute left-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border shadow-sm transition-colors',
                  isSelected
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white/90 text-transparent hover:border-primary-400 dark:border-gray-500 dark:bg-gray-900/80',
                )}
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={isSelected}
                  onChange={() => onToggleSelect(asset)}
                  aria-label={`Select ${asset.original_filename}`}
                />
                <CheckIcon className="h-4 w-4" />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}
